import * as React from "react";
import { cn } from "../lib/cn";

/**
 * Collapse — the grid-rows drawer (PQA#2642; ported from the unified
 * approve-card one-off per the ui-first directive). `open` renders the row at
 * `1fr`, closed at `0fr`, and the height change is a plain CSS transition:
 * no measured heights, no keyframes, no consumer animation wiring.
 *
 *   <Collapse open={!settled}>...</Collapse>
 *   <Collapse open={!settled} delayMs={100}>...</Collapse>  // two-beat close
 *
 * `delayMs` holds the height move so a sibling exit can lead — the
 * approve-card close reads as two beats because its AnimatedSeparator
 * collapses first and the drawer follows a beat later. Content stays MOUNTED
 * while closed (height 0, clipped): the consumer owns unmounting (the
 * approve-card's refetch removes the whole park), so closing is never a
 * data decision here. While closed the shell carries the `inert` attribute,
 * so the visually-hidden children leave the tab order and the a11y tree
 * (ui#232 review: height-0 + overflow-hidden alone kept an invisible button
 * reachable by keyboard). Motion-reduce drops the transition: open/closed
 * snap.
 */
export interface CollapseProps extends React.HTMLAttributes<HTMLDivElement> {
  /** True renders the content at full height; false collapses to zero. */
  open: boolean;
  /** Milliseconds to hold before the height moves (the two-beat stagger). */
  delayMs?: number;
}

export const Collapse = React.forwardRef<HTMLDivElement, CollapseProps>(
  ({ open, delayMs = 0, className, style, children, ...props }, ref) => {
    const localRef = React.useRef<HTMLDivElement | null>(null);
    const setRefs = (node: HTMLDivElement | null) => {
      localRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };
    // Attribute-level toggle so it works on every React version the lib
    // supports (the typed `inert` prop only landed in React 19).
    React.useEffect(() => {
      localRef.current?.toggleAttribute("inert", !open);
    }, [open]);
    return (
    <div
      ref={setRefs}
      className={cn(
        "grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
        open ? "[grid-template-rows:1fr]" : "[grid-template-rows:0fr]",
        className,
      )}
      style={delayMs > 0 ? { transitionDelay: `${delayMs}ms`, ...style } : style}
      {...props}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
    );
  },
);
Collapse.displayName = "Collapse";
